'use client'
import { type FC } from 'react'
import { Box, Flex, Image, Wrap, WrapItem } from '@chakra-ui/react'
import { GrandImage, ParsedHtml } from 'ui'
import {
  type PaymentMethodId,
  type PaymentMethodLogosCaptionPlacement,
  type PaymentMethodLogosBreakpoints,
  type PaymentMethodLogosSurface
} from 'ecosystem'
import { type StaticImageData } from 'next/image'
import { PAYMENT_METHOD_LABELS, resolvePaymentMethodMark } from './assets'
import { type PaymentStripId, resolvePaymentStrip } from './strips'

/** A mark resolved to something `next/image` can render: a bundled asset or a URL. */
interface ResolvedMark {
  id: PaymentMethodId
  src: string | StaticImageData
  /** Percentage of the surface height, defaulting to 100. */
  scale?: number
}

export interface PaymentMethodLogosProps {
  /** The surface's own slice of the store config. */
  config?: PaymentMethodLogosSurface
  /**
   * Contrast of the surface *behind* this strip, in the same sense as `useContrastTheme`:
   * `'light'` means light content on a dark background, so the inverse marks are used.
   */
  contrast?: 'dark' | 'light'
  /**
   * Fallback height in px when the surface config sets none. Widths stay proportional.
   */
  height?: number
}

/** Space between marks when the surface sets none, in px. */
const DEFAULT_GAP = 12

/**
 * Expand a per-breakpoint measurement into a Chakra responsive value.
 *
 * Each breakpoint falls back to the next one down, so a merchant who sets only
 * `desktop` gets that value everywhere rather than a 24px phone and a 40px desktop.
 */
const resolveBreakpoints = (
  config: PaymentMethodLogosBreakpoints | undefined,
  fallback: number,
  scale = 100
) => {
  const factor = (scale || 100) / 100
  const desktop = config?.desktop ?? fallback
  const tablet = config?.tablet ?? desktop
  const mobile = config?.mobile ?? tablet

  return {
    base: `${mobile * factor}px`,
    md: `${tablet * factor}px`,
    lg: `${desktop * factor}px`
  }
}

/** `left`/`right` put the caption beside the marks; `top`/`bottom` stack it. */
const DIRECTION: Record<
  PaymentMethodLogosCaptionPlacement,
  'row' | 'row-reverse' | 'column' | 'column-reverse'
> = {
  left: 'row',
  right: 'row-reverse',
  top: 'column',
  bottom: 'column-reverse'
}

/**
 * A row of accepted payment-method marks, optionally preceded by merchant copy.
 *
 * Replaces the single payment-*provider* badge the footer and PDP used to show. With an
 * orchestrator the provider is the wrong thing to advertise — a shopper cares that they
 * can pay with Swish or a card, not which company routes it — and Briqpay has no badge
 * of its own to display.
 *
 * Renders nothing unless the merchant has configured something, so storefronts that have
 * not opted in are unaffected.
 */
export const PaymentMethodLogos: FC<PaymentMethodLogosProps> = ({
  config,
  contrast = 'dark',
  height = 24
}) => {
  const isDarkSurface = contrast === 'light'
  const { methods, caption, captionPlacement = 'left' } = config ?? {}
  // Chakra style props, not `style`: `GrandImage` allowlists the props it forwards and
  // `style` is not among them, so an inline style silently never reaches the image and
  // every mark renders at its intrinsic size.
  const markHeight = resolveBreakpoints(config?.height, height)
  const markGap = resolveBreakpoints(config?.gap, DEFAULT_GAP)

  // An upload wins over a bundled provider strip, which in turn wins over the marks.
  const uploaded = isDarkSurface
    ? (config?.combinedImageDarkUrl ?? config?.combinedImageUrl)
    : config?.combinedImageUrl
  const preset = uploaded
    ? undefined
    : resolvePaymentStrip(config?.combinedPreset as PaymentStripId | undefined, isDarkSurface)
  const combined = uploaded ?? preset

  // An uploaded strip replaces the marks entirely — the merchant has opted out of our
  // composition, so do not second-guess it.
  const marks = combined
    ? null
    : (methods ?? []).flatMap<ResolvedMark>((item) => {
        const { id } = item
        const overrideUrl = isDarkSurface ? (item.imageDarkUrl ?? item.imageUrl) : item.imageUrl

        if (overrideUrl) {
          // A merchant's own artwork is taken at face value — no tone resolution, since
          // we cannot know what is in it.
          return [{ id, src: overrideUrl, scale: item.scale }]
        }

        // The item's own choice first, then the surface-wide tone, then `auto`.
        const image = resolvePaymentMethodMark(
          id,
          { lockup: item.lockup, tone: item.tone ?? config?.tone },
          isDarkSurface
        )

        // No asset for this id — skip it rather than render a broken image.
        return image ? [{ id, src: image, scale: item.scale }] : []
      })

  if (!combined && !marks?.length) {
    return null
  }

  const isRow = captionPlacement === 'left' || captionPlacement === 'right'

  return (
    <Flex
      align={isRow ? 'center' : 'flex-start'}
      // A long caption beside the marks squeezes them into a ragged column on narrow
      // viewports, so horizontal placements stack below the small breakpoint.
      direction={
        isRow ? { base: 'column', sm: DIRECTION[captionPlacement] } : DIRECTION[captionPlacement]
      }
      gap={3}
      w="100%"
    >
      {caption ? (
        <Box flexShrink={0} fontSize="sm">
          <ParsedHtml html={caption} />
        </Box>
      ) : null}

      {combined ? (
        <Image
          alt="Payment methods"
          h={markHeight}
          // Same reasoning as the individual marks: an uploaded strip has no intrinsic
          // size we can know, so let the file supply its own aspect.
          maxW="100%"
          objectFit="contain"
          src={typeof combined === 'string' ? combined : combined.src}
          w="auto"
        />
      ) : (
        <Wrap align="center" spacing={markGap}>
          {marks?.map((mark, index) => {
            const scale = mark.scale && mark.scale !== 100 ? mark.scale : undefined
            const isOwnArtwork = typeof mark.src === 'string'

            // Fixed height with proportional width is what keeps a row of
            // mixed-aspect marks looking deliberate.
            const h = scale ? resolveBreakpoints(config?.height, height, scale) : markHeight
            // Safety rail: past ten times its height it is not a logo, and uncapped one
            // mis-picked image runs the width of the footer.
            const maxW = resolveBreakpoints(config?.height, height, (scale ?? 100) * 10)

            return (
              <WrapItem key={`${mark.id}-${index}`}>
                {isOwnArtwork ? (
                  // A plain `img` for a merchant's own artwork, deliberately not
                  // `next/image`: that needs intrinsic dimensions, and for an arbitrary
                  // URL any value we invent becomes the element's aspect ratio — which
                  // stretched a square photo across a 4:1 box. A bare image takes its
                  // aspect from the file, so `width: auto` is simply correct.
                  <Image
                    alt={PAYMENT_METHOD_LABELS[mark.id] ?? mark.id}
                    h={h}
                    maxW={maxW}
                    objectFit="contain"
                    src={mark.src as string}
                    w="auto"
                  />
                ) : (
                  <GrandImage
                    alt={PAYMENT_METHOD_LABELS[mark.id] ?? mark.id}
                    h={h}
                    src={mark.src}
                    w="auto"
                  />
                )}
              </WrapItem>
            )
          })}
        </Wrap>
      )}
    </Flex>
  )
}
